import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { generateAIResponse, SUGGESTIONS, type ChatMessage, type Lang } from './ChatWidgetAI';

const WHATSAPP_NUMBER = '41765593993';

const INITIAL_MESSAGES: Record<Lang, ChatMessage[]> = {
  de: [
    {
      id: 'welcome-de',
      role: 'assistant',
      content: 'Hallo! Ich bin der Esteva Assistent. Ich beantworte Ihnen gerne Fragen zu unseren Behandlungen, Preisen und Terminen. Wie kann ich Ihnen helfen?',
      isHtml: false,
    },
  ],
  en: [
    {
      id: 'welcome-en',
      role: 'assistant',
      content: 'Hello! I am the Esteva Assistant. I am happy to answer your questions about our treatments, prices, and appointments. How can I help you?',
      isHtml: false,
    },
  ],
  al: [
    {
      id: 'welcome-al',
      role: 'assistant',
      content: 'Përshëndetje! Unë jam Asistenti Esteva. Jam i gatshëm t\'u përgjigjem pyetjeve rreth trajtimeve tona, çmimeve dhe termineve. Si mund t\'ju ndihmoj?',
      isHtml: false,
    },
  ],
  fr: [
    {
      id: 'welcome-fr',
      role: 'assistant',
      content: 'Bonjour ! Je suis l\'assistant Esteva. Je réponds volontiers à vos questions sur nos traitements, nos prix et les rendez-vous. Comment puis-je vous aider ?',
      isHtml: false,
    },
  ],
  it: [
    {
      id: 'welcome-it',
      role: 'assistant',
      content: 'Ciao! Sono l\'assistente Esteva. Rispondo volentieri alle tue domande su trattamenti, prezzi e appuntamenti. Come posso aiutarti?',
      isHtml: false,
    },
  ],
};

export default function ChatWidget() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(INITIAL_MESSAGES.de);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [lang, setLang] = useState<Lang>('de');
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  const handleSend = useCallback(
    (text: string) => {
      if (!text.trim()) return;

      const userMsg: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: text.trim(),
        isHtml: false,
      };

      setMessages((prev) => [...prev, userMsg]);
      setInputValue('');
      setIsTyping(true);
      setShowSuggestions(false);

      const aiResponse = generateAIResponse(text.trim());
      setLang(aiResponse.lang);

      // Simulate typing delay
      const delay = Math.min(800 + text.trim().length * 15, 2500);
      setTimeout(() => {
        const assistantMsg: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: aiResponse.content,
          isHtml: aiResponse.isHtml,
        };
        setMessages((prev) => [...prev, assistantMsg]);
        setIsTyping(false);
      }, delay);
    },
    []
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSend(inputValue);
    }
  };

  const handleSuggestionClick = (value: string) => {
    handleSend(value);
  };

  const handleWhatsApp = () => {
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(t('pub.whatsapp_default_msg'))}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleBooking = () => {
    setIsOpen(false);
    navigate('/booking');
  };

  const handleReset = () => {
    setMessages(INITIAL_MESSAGES[lang]);
    setShowSuggestions(true);
  };

  const currentSuggestions = SUGGESTIONS[lang] || SUGGESTIONS.de;

  return (
    <>
      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed z-[60] flex flex-col overflow-hidden"
          style={{
            bottom: '148px',
            left: '20px',
            width: '380px',
            maxWidth: 'calc(100vw - 40px)',
            height: '540px',
            maxHeight: 'calc(100vh - 180px)',
            background: 'oklch(var(--background-50))',
            border: '1px solid oklch(var(--background-200))',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center gap-3 px-4 py-3 shrink-0"
            style={{
              background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
            }}
          >
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 shrink-0">
              <i className="ri-psychotherapy-line text-white text-lg"></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white leading-tight truncate">{t('shared.chat_title')}</div>
              <div className="text-[11px] text-white/80 leading-tight flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block"></span>
                {t('shared.chat_online')}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleReset}
                className="w-8 h-8 flex items-center justify-center rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                aria-label={t('shared.chat_restart')}
                title={t('shared.chat_restart')}
              >
                <i className="ri-refresh-line text-sm"></i>
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                aria-label={t('shared.close')}
                title={t('shared.close')}
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: 'oklch(var(--background-50))' }}>
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 text-[13px] leading-relaxed ${
                    msg.role === 'user'
                      ? 'text-white rounded-2xl rounded-br-md'
                      : 'text-foreground-800 rounded-2xl rounded-bl-md'
                  }`}
                  style={{
                    background:
                      msg.role === 'user'
                        ? 'oklch(var(--primary-500))'
                        : 'oklch(var(--background-100))',
                  }}
                >
                  {msg.isHtml ? (
                    <div dangerouslySetInnerHTML={{ __html: msg.content }} />
                  ) : (
                    <div className="whitespace-pre-wrap">{msg.content}</div>
                  )}
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div
                  className="px-3.5 py-2.5 rounded-2xl rounded-bl-md"
                  style={{ background: 'oklch(var(--background-100))' }}
                >
                  <div className="flex items-center gap-1">
                    <span
                      className="w-1.5 h-1.5 rounded-full animate-bounce"
                      style={{ background: 'oklch(var(--foreground-400))', animationDelay: '0ms' }}
                    ></span>
                    <span
                      className="w-1.5 h-1.5 rounded-full animate-bounce"
                      style={{ background: 'oklch(var(--foreground-400))', animationDelay: '150ms' }}
                    ></span>
                    <span
                      className="w-1.5 h-1.5 rounded-full animate-bounce"
                      style={{ background: 'oklch(var(--foreground-400))', animationDelay: '300ms' }}
                    ></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Suggestions */}
          {showSuggestions && !isTyping && messages.length <= 1 && (
            <div className="px-4 pb-3 shrink-0">
              <div className="flex flex-wrap gap-2">
                {currentSuggestions.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSuggestionClick(s.value)}
                    className="px-3 py-1.5 text-xs font-medium rounded-full border cursor-pointer transition-colors whitespace-nowrap"
                    style={{
                      borderColor: '#C4B5FD',
                      color: '#6D28D9',
                      background: '#EDE9FE',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#DDD6FE';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#EDE9FE';
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="px-4 py-2 border-t flex gap-2 shrink-0" style={{ borderColor: 'oklch(var(--background-200))' }}>
            <button
              onClick={handleBooking}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg cursor-pointer transition-colors whitespace-nowrap"
              style={{
                background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
                color: 'white',
              }}
            >
              <i className="ri-calendar-check-line"></i>
              {t('shared.book_appointment')}
            </button>
            <button
              onClick={handleWhatsApp}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg cursor-pointer transition-colors whitespace-nowrap"
              style={{
                background: '#25d366',
                color: 'white',
              }}
            >
              <i className="ri-whatsapp-line"></i>
              WhatsApp
            </button>
          </div>

          {/* Input Area */}
          <div className="px-3 py-3 border-t shrink-0" style={{ borderColor: 'oklch(var(--background-200))' }}>
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('shared.chat_input_placeholder')}
                className="flex-1 text-sm px-3.5 py-2.5 rounded-xl outline-none"
                style={{
                  background: 'oklch(var(--background-100))',
                  color: 'oklch(var(--foreground-800))',
                }}
              />
              <button
                onClick={() => handleSend(inputValue)}
                disabled={!inputValue.trim() || isTyping}
                className="w-10 h-10 flex items-center justify-center rounded-xl cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: inputValue.trim() && !isTyping
                    ? '#7C3AED'
                    : 'oklch(var(--background-200))',
                }}
                aria-label={t('shared.send')}
              >
                <i className="ri-send-plane-fill text-white text-base"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed z-[60] flex items-center justify-center rounded-full cursor-pointer transition-transform duration-200 hover:scale-110"
        style={{
          bottom: '88px',
          left: '20px',
          width: '56px',
          height: '56px',
          background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
          boxShadow: '0 4px 20px rgba(124,58,237,0.4)',
        }}
        aria-label={isOpen ? t('shared.chat_close') : t('shared.chat_open')}
      >
        {isOpen ? (
          <i className="ri-close-line text-white text-2xl"></i>
        ) : (
          <i className="ri-chat-1-line text-white text-2xl"></i>
        )}
        {/* Pulse ring when closed */}
        {!isOpen && (
          <span
            className="absolute w-14 h-14 rounded-full animate-ping"
            style={{ background: 'rgba(124,58,237,0.3)' }}
          ></span>
        )}
      </button>
    </>
  );
}